"use client";

import { Suspense, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useInvoiceStore } from "@/lib/invoice/store";
import { compareText, fmtMoney, num, parsePastedLines, projectLine, uid } from "@/lib/invoice/utils";
import { Button, Combobox, Empty, Field, Input, Loading, Modal, PageHeader, Select, Textarea, cx, useFeedback } from "@/components/Invoice/ui";
import { listIssues } from "@/lib/invoice/projectHealth";
import { BILLING_OPTIONS, PROJECT_STATUS } from "@/components/Invoice/options";
import { TagChip, TagSelect } from "@/components/Invoice/Tags";
import { RowImportButton } from "@/components/Invoice/RowImport";
import { suggestTag, tagById } from "@/lib/invoice/tags";
import { autoTaxMode } from "@/components/Invoice/InvoiceEditor";
import { TAX_MODES } from "@/lib/invoice/utils";

const BLANK = {
  kind: "project", clientId: "", name: "", service: "", billing: "monthly", rate: "", qty: 1, taxRate: 18, hsn: "",
  status: "active", startDate: "", endDate: "", notes: "", tag: "",
};

const STATUS_CLS = {
  active: "bg-emerald-100 text-emerald-700",
  paused: "bg-amber-100 text-amber-800",
  completed: "bg-slate-200 text-slate-600",
};

function ProjectsInner() {
  const store = useInvoiceStore();
  const router = useRouter();
  const params = useSearchParams();
  const [clientId, setClientId] = useState(params.get("client") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [billing, setBilling] = useState(params.get("billing") || "");
  const [cleanup, setCleanup] = useState(false);
  const [bulkStart, setBulkStart] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [kind, setKind] = useState(params.get("kind") || "");
  const [q, setQ] = useState(params.get("q") || "");
  const { toast, confirm } = useFeedback();
  const [editing, setEditing] = useState(null);
  const [bulk, setBulk] = useState(null);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (store.ready && params.get("new")) openNew({ kind: params.get("kind") === "service" ? "service" : "project" }); // eslint-disable-line no-use-before-define
  }, [store.ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const clientName = useMemo(() => Object.fromEntries(store.clients.map((c) => [c.id, c.name])), [store.clients]);
  const clientCur = useMemo(
    () => Object.fromEntries(store.clients.map((c) => [c.id, c.currency || store.settings.defaults.currency])),
    [store.clients, store.settings]
  );

  if (!store.ready) return <Loading />;

  // tax follows the client: export / no-tax clients never get GST on their projects
  const taxOf = (cid) => {
    const c = store.clients.find((x) => x.id === cid);
    if (!c) return { mode: store.settings.defaults.taxMode, taxable: true, client: null };
    const mode = autoTaxMode(c, store.settings, store.brandById(c.brandId));
    return { mode, taxable: mode === "intra" || mode === "inter" || mode === "vat", client: c };
  };
  const withClient = (e, cid) => {
    const t = taxOf(cid);
    return { ...e, clientId: cid, taxRate: !t.taxable ? 0 : num(e.taxRate) ? e.taxRate : store.settings.defaults.taxRate };
  };
  const openNew = (patch = {}) => setEditing(withClient({ ...BLANK, taxRate: store.settings.defaults.taxRate, ...patch }, patch.clientId ?? clientId));

  const kindOf = (p) => p.kind || "project";
  const counts = { "": store.projects.length, project: store.projects.filter((p) => kindOf(p) === "project").length, service: store.projects.filter((p) => kindOf(p) === "service").length };
  const list = store.projects
    .filter((p) => !kind || kindOf(p) === kind)
    .filter((p) => (!clientId || p.clientId === clientId || (!p.clientId && kindOf(p) === "service")) && (!status || p.status === status) && (!billing || p.billing === billing))
    .filter((p) => !tagFilter || (tagFilter === "none" ? !p.tag : p.tag === tagFilter))
    .filter((p) => `${projectLine(p)} ${clientName[p.clientId] || ""}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => compareText(clientName[a.clientId] || "", clientName[b.clientId] || "") || compareText(projectLine(a), projectLine(b)));

  const monthlyByCur = {};
  for (const p of list) {
    if (p.status !== "active" || p.billing !== "monthly" || !p.clientId) continue;
    const cur = clientCur[p.clientId] || "GBP";
    monthlyByCur[cur] = (monthlyByCur[cur] || 0) + num(p.rate) * (num(p.qty) || 1);
  }

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const allSelected = list.length > 0 && list.every((p) => selected.has(p.id));

  const save = () => {
    const isService = editing.kind === "service";
    if (!editing.clientId && !isService) return toast("Choose a client (or make it a service offered to all clients)", { tone: "error" });
    if (!editing.name.trim()) return toast(`${isService ? "Service" : "Project"} name is required`, { tone: "error" });
    store.saveProject({ ...editing, rate: num(editing.rate), qty: num(editing.qty) || 1, taxRate: num(editing.taxRate) });
    setEditing(null);
    toast(`${isService ? "Service" : "Project"} saved`, { tone: "success" });
  };

  const removeProjects = async (ids) => {
    const ok = await confirm(`Delete ${ids.length} project(s)? Existing invoices are not affected. You can restore from Settings → Recycle bin.`, {
      title: "Delete projects",
      okLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    const tid = store.deleteProjects(ids);
    setSelected(new Set());
    setEditing(null);
    toast(`${ids.length} project(s) deleted`, { action: { label: "Undo", onClick: () => store.restoreTrash(tid) } });
  };

  const bulkUpdate = (patch, what) => {
    store.saveProjects(store.projects.filter((p) => selected.has(p.id)).map((p) => ({ ...p, ...patch })));
    toast(`${what} – ${selected.size} item(s) updated`, { tone: "success" });
    setSelected(new Set());
  };
  const bulkSetStatus = (s) => bulkUpdate({ status: s }, `Marked ${s}`);
  const issues = listIssues(store.projects);

  const invoiceSelected = () => {
    const chosen = store.projects.filter((p) => selected.has(p.id));
    const clients = new Set(chosen.map((p) => p.clientId || clientId).filter(Boolean));
    if (clients.size !== 1) return toast("Select items from a single client (or filter by the client first) to create one invoice.", { tone: "error" });
    router.push(`/invoice/invoices/edit?client=${[...clients][0]}&projects=${chosen.map((p) => p.id).join(",")}`);
  };

  const saveBulk = () => {
    if (!bulk.clientId && bulk.kind !== "service") return toast("Choose a client", { tone: "error" });
    const rows = parsePastedLines(bulk.text);
    if (!rows.length) return toast("Paste at least one line", { tone: "error" });
    store.saveProjects(
      rows.map((r) => ({
        ...BLANK,
        id: uid(),
        kind: bulk.kind || "project",
        clientId: bulk.clientId,
        name: r.description,
        tag: suggestTag(r.description, store.settings.tags),
        rate: r.rate,
        qty: r.qty || 1,
        billing: bulk.billing,
        taxRate: store.settings.defaults.taxRate,
      }))
    );
    setBulk(null);
    toast(`Added ${rows.length} ${bulk.kind === "service" ? "services" : "projects"}`, { tone: "success" });
  };

  return (
    <div>
      <PageHeader
        title="Projects & services"
        subtitle="Each project or service becomes an invoice line. Services can belong to one client or be offered to every client. Monthly retainers are billed in one click."
        actions={
          <>
            {store.projects.some((p) => !p.tag) && (
              <Button
                variant="secondary"
                onClick={() => {
                  const updated = store.projects.filter((p) => !p.tag).map((p) => ({ ...p, tag: suggestTag(`${p.name} ${p.service || ""}`, store.settings.tags) })).filter((p) => p.tag);
                  store.saveProjects(updated);
                  toast(`Categorised ${updated.length} project(s) automatically`, { tone: "success" });
                }}
              >
                Auto-categorise
              </Button>
            )}
            <RowImportButton kind="projects" defaultClientId={clientId} />
            <Button variant="secondary" onClick={() => setBulk({ clientId, billing: "monthly", text: "", kind: kind || "project" })}>
              Bulk add (paste)
            </Button>
            <Button variant="secondary" onClick={() => openNew({ kind: "service" })}>+ Add service</Button>
            <Button onClick={() => openNew()}>+ Add project</Button>
          </>
        }
      />

      {issues.count > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200" data-testid="cleanup-banner">
          <span>
            <b>Tidy up your list:</b>{" "}
            {[issues.junk.length && `${issues.junk.length} entr${issues.junk.length === 1 ? "y looks" : "ies look"} like bank / tax / label text from an invoice`, issues.dups.length && `${issues.dups.reduce((n, g) => n + g.remove.length, 0)} duplicate(s)`].filter(Boolean).join(" and ")}
            .
          </span>
          <Button size="sm" className="ml-auto" onClick={() => setCleanup(true)} data-testid="cleanup-open">
            Review &amp; clean up
          </Button>
        </div>
      )}

      {store.projects.length > 0 && (
        <Overview
          store={store}
          clientName={clientName}
          clientCur={clientCur}
          kindOf={kindOf}
          onClient={setClientId}
          onTag={setTagFilter}
          onKind={(k) => {
            setKind(k);
            setStatus("");
          }}
          onActive={() => setStatus("active")}
          onMonthly={() => {
            setBilling("monthly");
            setStatus("active");
          }}
        />
      )}

      <div className="mb-3 inline-flex rounded-lg bg-slate-100 p-1 text-sm" role="tablist">
        {[["", "All"], ["project", "Projects"], ["service", "Services"]].map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={kind === k}
            onClick={() => setKind(k)}
            className={cx("rounded-md px-3 py-1.5 font-medium transition", kind === k ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-800")}
          >
            {label} <span className="text-xs text-slate-400">{counts[k]}</span>
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="max-w-xs" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">All clients</option>
          {store.clients.filter((c) => !c.archived || c.id === clientId).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select className="max-w-[160px]" value={status} onChange={(e) => setStatus(e.target.value)} options={[{ value: "", label: "Any status" }, ...PROJECT_STATUS]} />
        <Select className="max-w-[190px]" value={billing} onChange={(e) => setBilling(e.target.value)} options={[{ value: "", label: "Any billing" }, ...BILLING_OPTIONS]} data-testid="billing-filter" />
        <Select className="max-w-[180px]" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
          <option value="">All categories</option>
          {store.settings.tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          <option value="none">Uncategorised</option>
        </Select>
        <Input className="max-w-xs" placeholder="Search projects & services…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="ml-auto text-sm text-slate-600">
          {list.length} item{list.length === 1 ? "" : "s"} · Monthly run-rate:{" "}
          <b>{Object.entries(monthlyByCur).map(([c, v]) => fmtMoney(v, c)).join(" + ") || "—"}</b>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-brand-50 px-3 py-2 text-sm">
          <b>{selected.size} selected</b>
          <Button size="sm" onClick={invoiceSelected}>Create invoice</Button>
          <Button size="sm" variant="secondary" onClick={() => bulkSetStatus("active")}>Mark active</Button>
          <Button size="sm" variant="secondary" onClick={() => bulkSetStatus("paused")}>Pause</Button>
          <Button size="sm" variant="secondary" onClick={() => bulkSetStatus("completed")}>Complete</Button>
          <Select
            className="!w-auto !py-1 text-xs"
            value=""
            onChange={(e) => e.target.value && bulkUpdate({ billing: e.target.value }, `Billing set to ${BILLING_OPTIONS.find((b) => b.value === e.target.value)?.label}`)}
            data-testid="bulk-billing"
          >
            <option value="">Set billing…</option>
            {BILLING_OPTIONS.map((b) => (
              <option key={b.value} value={b.value}>{b.label}</option>
            ))}
          </Select>
          <span className="inline-flex items-center gap-1">
            <Input type="date" className="!w-36 !py-1 text-xs" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)} aria-label="Start date for selected" data-testid="bulk-start" />
            <Button size="sm" variant="secondary" disabled={!bulkStart} onClick={() => bulkUpdate({ startDate: bulkStart }, "Start date set")}>
              Set start
            </Button>
          </span>
          <TagSelect
            tags={store.settings.tags}
            value=""
            placeholder="Set category…"
            onChange={(tag) => {
              store.saveProjects(store.projects.filter((p) => selected.has(p.id)).map((p) => ({ ...p, tag })));
              toast(`Category set on ${selected.size} project(s)`);
              setSelected(new Set());
            }}
          />
          <Button
            size="sm"
            variant="danger"
            onClick={() => removeProjects([...selected])}
          >
            Delete
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      {store.clients.length === 0 && kind !== "service" && list.length === 0 ? (
        <Empty title="Add a client first">Projects belong to a client – create one on the Clients page. Services can be added any time.</Empty>
      ) : list.length === 0 ? (
        <Empty title={kind === "service" ? "No services yet" : "Nothing found"}>
          {kind === "service"
            ? "Add the services you sell (e.g. SEO – 10 keywords, Google Ads management) – for one client or for every client."
            : "Add projects or services one by one, or paste a list from Excel with “Bulk add”."}
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200/80 bg-white shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => setSelected(allSelected ? new Set() : new Set(list.map((p) => p.id)))}
                  />
                </th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Billing</th>
                <th className="px-4 py-3">Started</th>
                <th className="px-4 py-3 text-right">Rate</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((p) => (
                <tr key={p.id} className={cx("hover:bg-slate-50", selected.has(p.id) && "bg-brand-50/40")}>
                  <td className="px-4 py-2.5">
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{p.name}</div>
                    {p.service && <div className="text-xs text-slate-500">{p.service}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={cx("rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide", kindOf(p) === "service" ? "bg-violet-50 text-violet-700" : "bg-sky-50 text-sky-700")}>{kindOf(p)}</span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{p.clientId ? clientName[p.clientId] || "—" : <span className="italic text-slate-500">Any client</span>}</td>
                  <td className="px-4 py-2.5">{p.tag ? <TagChip tag={tagById(store.settings.tags, p.tag)} /> : <span className="text-xs text-slate-400">—</span>}</td>
                  <td className="px-4 py-2.5">
                    <BillingPill billing={p.billing} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <StartPill p={p} />
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium">
                    {fmtMoney(num(p.rate), clientCur[p.clientId])}
                    {num(p.qty) > 1 && <span className="text-xs text-slate-500"> × {p.qty}</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={cx("rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize", STATUS_CLS[p.status])}>{p.status}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="secondary" onClick={() => setEditing({ ...BLANK, ...p })}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={`${editing?.id ? "Edit" : "New"} ${editing?.kind === "service" ? "service" : "project"}`}
        wide
        footer={
          <>
            {editing?.id && (
              <Button
                variant="danger"
                className="mr-auto"
                onClick={() => removeProjects([editing.id])}
              >
                Delete
              </Button>
            )}
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save}>Save {editing?.kind === "service" ? "service" : "project"}</Button>
          </>
        }
      >
        {editing && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Type" className="sm:col-span-2">
              <div className="inline-flex rounded-lg bg-slate-100 p-1 text-sm">
                {[["project", "Project", "Work for one client – e.g. a website or an end-client account"], ["service", "Service", "Something you sell – for one client or every client"]].map(([k, label, tip]) => (
                  <button
                    key={k}
                    type="button"
                    title={tip}
                    onClick={() => setEditing({ ...editing, kind: k })}
                    className={cx("rounded-md px-3 py-1.5 font-medium transition", (editing.kind || "project") === k ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-800")}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={editing.kind === "service" ? "Client" : "Client *"} className="sm:col-span-2" hint={editing.kind === "service" ? "Leave as “Any client” to offer this service to every client" : undefined}>
              <Select value={editing.clientId} onChange={(e) => setEditing(withClient(editing, e.target.value))}>
                <option value="">{editing.kind === "service" ? "Any client (reusable service)" : "Choose client…"}</option>
                {store.clients.filter((c) => !c.archived || c.id === clientId).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            {editing.kind !== "service" && !editing.id && store.projects.some((x) => x.kind === "service") && (
              <Field label="Start from your price list" className="sm:col-span-2" hint="Fills the service, price, billing and category – you only add the project name">
                <Combobox
                  strict
                  value=""
                  onChange={(id) => {
                    const sv = store.projects.find((x) => x.id === id);
                    if (!sv) return;
                    setEditing({ ...editing, service: [sv.name, sv.service].filter(Boolean).join(" "), rate: sv.rate, qty: sv.qty || 1, billing: sv.billing, tag: sv.tag || editing.tag, hsn: sv.hsn || editing.hsn, presetId: sv.id });
                  }}
                  options={store.projects
                    .filter((x) => x.kind === "service" && (!x.clientId || x.clientId === editing.clientId))
                    .sort((a, b) => compareText(projectLine(a), projectLine(b)))
                    .map((x) => ({ value: x.id, label: projectLine(x), hint: fmtMoney(num(x.rate), clientCur[editing.clientId] || store.settings.defaults.currency) }))}
                  placeholder="e.g. SEO – 3 keywords"
                  testId="preset"
                />
              </Field>
            )}
            <Field label={editing.kind === "service" ? "Service name *" : "Project / end-client name *"}>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder={editing.kind === "service" ? "SEO" : "Zenvy Wellness"} />
            </Field>
            <Field label="Category (tag)" hint="Used to filter invoices and report revenue by service">
              <TagSelect
                className="w-full py-2 text-sm"
                tags={store.settings.tags}
                value={editing.tag || ""}
                onChange={(tag) => setEditing({ ...editing, tag })}
              />
              {!editing.tag && editing.name && suggestTag(`${editing.name} ${editing.service || ""}`, store.settings.tags) && (
                <button
                  className="mt-1 text-[11px] text-brand-700 hover:underline"
                  onClick={() => setEditing({ ...editing, tag: suggestTag(`${editing.name} ${editing.service || ""}`, store.settings.tags) })}
                >
                  Suggest: {tagById(store.settings.tags, suggestTag(`${editing.name} ${editing.service || ""}`, store.settings.tags))?.name}
                </button>
              )}
            </Field>
            <Field label={editing.kind === "service" ? "Details / package" : "Service"} hint="Appended to the invoice line, e.g. “20 Keywords”, “Meta ads”">
              <Input value={editing.service} onChange={(e) => setEditing({ ...editing, service: e.target.value })} />
            </Field>
            <Field label="Billing type">
              <Select value={editing.billing} onChange={(e) => setEditing({ ...editing, billing: e.target.value })} options={BILLING_OPTIONS} />
            </Field>
            <Field label="Status">
              <Select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} options={PROJECT_STATUS} />
            </Field>
            <Field label={editing.billing === "hourly" ? "Hourly rate" : "Rate"}>
              <Input type="number" step="0.01" value={editing.rate} onChange={(e) => setEditing({ ...editing, rate: e.target.value })} />
            </Field>
            <Field label={editing.billing === "hourly" ? "Default hours" : "Quantity"}>
              <Input type="number" step="0.01" value={editing.qty} onChange={(e) => setEditing({ ...editing, qty: e.target.value })} />
            </Field>
            {(() => {
              const t = taxOf(editing.clientId);
              if (!t.taxable)
                return (
                  <Field label="Tax" hint={t.client ? `${t.client.name}: ${TAX_MODES[t.mode]?.label || "no tax"} (${t.client.currency || store.settings.defaults.currency}). Change it on the client.` : undefined}>
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">No GST / tax for this client</div>
                  </Field>
                );
              return (
                <Field label={t.mode === "vat" ? "VAT / tax rate %" : "GST rate %"}>
                  <Input type="number" step="0.01" value={editing.taxRate} onChange={(e) => setEditing({ ...editing, taxRate: e.target.value })} />
                </Field>
              );
            })()}
            <Field label="HSN / SAC code">
              <Input value={editing.hsn} onChange={(e) => setEditing({ ...editing, hsn: e.target.value })} placeholder={store.settings.defaults.sac} />
            </Field>
            <Field label="Start date" hint={editing.billing === "monthly" ? "Starting mid-month? The first invoice is pro-rated automatically" : undefined}>
              <Input type="date" value={editing.startDate} onChange={(e) => setEditing({ ...editing, startDate: e.target.value })} />
            </Field>
            <Field label="End date">
              <Input type="date" value={editing.endDate} onChange={(e) => setEditing({ ...editing, endDate: e.target.value })} />
            </Field>
            <Field label="Notes" className="sm:col-span-2">
              <Textarea value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
            </Field>
          </div>
        )}
      </Modal>

      {cleanup && <CleanupModal issues={issues} clientName={clientName} clientCur={clientCur} store={store} onClose={() => setCleanup(false)} />}

      <Modal
        open={!!bulk}
        onClose={() => setBulk(null)}
        title={`Bulk add ${bulk?.kind === "service" ? "services" : "projects"}`}
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setBulk(null)}>Cancel</Button>
            <Button onClick={saveBulk}>Add {bulk ? parsePastedLines(bulk.text).length : 0} {bulk?.kind === "service" ? "services" : "projects"}</Button>
          </>
        }
      >
        {bulk && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Type">
                <Select value={bulk.kind || "project"} onChange={(e) => setBulk({ ...bulk, kind: e.target.value })} options={[{ value: "project", label: "Projects" }, { value: "service", label: "Services" }]} />
              </Field>
              <Field label={bulk.kind === "service" ? "Client" : "Client *"}>
                <Select value={bulk.clientId} onChange={(e) => setBulk({ ...bulk, clientId: e.target.value })}>
                  <option value="">{bulk.kind === "service" ? "Any client" : "Choose client…"}</option>
                  {store.clients.filter((c) => !c.archived || c.id === clientId).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Billing type">
                <Select value={bulk.billing} onChange={(e) => setBulk({ ...bulk, billing: e.target.value })} options={BILLING_OPTIONS} />
              </Field>
            </div>
            <Field
              label="Paste rows (from Excel or text)"
              hint="One project per line: “Name ⇥ Rate”, “Name ⇥ Qty ⇥ Rate”, or “Name £66”."
            >
              <Textarea
                className="min-h-[220px] font-mono text-xs"
                value={bulk.text}
                onChange={(e) => setBulk({ ...bulk, text: e.target.value })}
                placeholder={"Aakaar Child Development Centre 5 keywords\t66\nAntlop Google ads\t100"}
              />
            </Field>
          </div>
        )}
      </Modal>
    </div>
  );
}

const BILLING_CLS = {
  monthly: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  "one-time": "bg-slate-100 text-slate-700 ring-slate-200",
  hourly: "bg-teal-50 text-teal-700 ring-teal-200",
};
const BILLING_SHORT = { monthly: "Monthly", "one-time": "One-time", hourly: "Hourly" };

function BillingPill({ billing }) {
  return (
    <span className={cx("inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset", BILLING_CLS[billing] || BILLING_CLS["one-time"])}>
      {billing === "monthly" && <span aria-hidden>↻</span>}
      {BILLING_SHORT[billing] || billing || "—"}
    </span>
  );
}

const shortMonth = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { month: "short", year: "numeric" });
const shortDay = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

// "Since Jul 2026" / "Starts 16 Oct 2026" / "Ended Aug 2026" – readable at a glance.
function StartPill({ p }) {
  const today = new Date().toISOString().slice(0, 10);
  if (!p.startDate) return <span className="text-xs text-slate-400">—</span>;
  const future = p.startDate > today;
  const ended = p.endDate && p.endDate < today;
  const midMonth = !p.startDate.endsWith("-01");
  return (
    <div title={`Start ${shortDay(p.startDate)}${p.endDate ? ` · End ${shortDay(p.endDate)}` : ""}`}>
      <span
        className={cx(
          "inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
          ended ? "bg-slate-100 text-slate-600 ring-slate-200" : future ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-emerald-50 text-emerald-800 ring-emerald-200"
        )}
      >
        {ended ? `Ended ${shortMonth(p.endDate)}` : future ? `Starts ${shortDay(p.startDate)}` : `Since ${midMonth ? shortDay(p.startDate) : shortMonth(p.startDate)}`}
      </span>
    </div>
  );
}

// Totals that always match the tabs and table: every project / service is
// counted, with how many are active and what the active retainers bring in.
function Overview({ store, clientName, clientCur, kindOf, onClient, onTag, onKind, onActive, onMonthly }) {
  const [open, setOpen] = useState(false);
  const all = store.projects;
  const projects = all.filter((p) => kindOf(p) === "project");
  const services = all.filter((p) => kindOf(p) === "service");
  const active = (list) => list.filter((p) => p.status === "active").length;
  const cur = (p) => clientCur[p.clientId] || store.settings.defaults.currency;
  const monthly = {};
  let monthlyCount = 0;
  const byClient = new Map();
  for (const p of all) {
    const k = p.clientId || "";
    const e = byClient.get(k) || { projects: 0, services: 0, active: 0, monthly: 0 };
    e[kindOf(p) === "service" ? "services" : "projects"]++;
    if (p.status === "active") e.active++;
    if (p.status === "active" && p.billing === "monthly") {
      const v = num(p.rate) * (num(p.qty) || 1);
      e.monthly += v;
      monthly[cur(p)] = (monthly[cur(p)] || 0) + v;
      monthlyCount++;
    }
    byClient.set(k, e);
  }
  const clients = [...byClient.keys()].filter(Boolean);
  const activeClients = clients.filter((c) => byClient.get(c).active > 0).length;
  const byTag = new Map();
  for (const p of all) byTag.set(p.tag || "", (byTag.get(p.tag || "") || 0) + 1);
  const rows = [...byClient.entries()].sort((a, b) => b[1].monthly - a[1].monthly || b[1].projects + b[1].services - (a[1].projects + a[1].services));
  const tile = (label, value, sub, onClick, testId) => (
    <button type="button" onClick={onClick} className="rounded-xl bg-white px-4 py-3 text-left shadow-xs ring-1 ring-slate-200/80 transition hover:ring-slate-300" data-testid={testId}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-xl font-semibold tabular-nums text-slate-900">{value}</div>
      <div className="mt-0.5 text-xs text-slate-500">{sub}</div>
    </button>
  );
  return (
    <div className="mb-5 space-y-3" data-testid="projects-overview">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tile("Projects", projects.length, `${active(projects)} active`, () => onKind("project"), "tile-projects")}
        {tile("Services", services.length, `${active(services)} active`, () => onKind("service"), "tile-services")}
        {tile("Clients", clients.length, `${activeClients} with active work`, () => setOpen(true), "tile-clients")}
        {tile("Monthly retainers", Object.entries(monthly).map(([c, v]) => fmtMoney(v, c)).join(" + ") || "—", `${monthlyCount} active monthly`, onMonthly, "tile-monthly")}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {[...byTag.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([t, n]) => (
            <button key={t || "none"} onClick={() => onTag(t || "none")} className="rounded-full hover:opacity-80" title="Show only this category">
              {t ? <TagChip tag={tagById(store.settings.tags, t)} count={n} /> : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">Uncategorised · {n}</span>}
            </button>
          ))}
        <button className="ml-auto text-xs font-medium text-brand-700 hover:underline" onClick={() => setOpen((o) => !o)}>
          {open ? "Hide" : "Show"} per-client breakdown
        </button>
      </div>
      {open && (
        <div className="overflow-hidden rounded-xl bg-white ring-1 ring-slate-200/80">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2">Client</th>
                <th className="px-4 py-2 text-right">Projects</th>
                <th className="px-4 py-2 text-right">Services</th>
                <th className="px-4 py-2 text-right">Active</th>
                <th className="px-4 py-2 text-right">Monthly value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(([cid, e]) => (
                <tr key={cid || "any"} className="cursor-pointer hover:bg-slate-50" onClick={() => onClient(cid)}>
                  <td className="px-4 py-2">{cid ? clientName[cid] || "—" : <span className="italic text-slate-500">Any client (reusable services)</span>}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{e.projects}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{e.services}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{e.active}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{e.monthly ? fmtMoney(e.monthly, clientCur[cid] || store.settings.defaults.currency) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Review screen for junk lines and duplicates already in the list. Suggested
// removals are pre-ticked; deleting goes to the recycle bin (undo available).
function CleanupModal({ issues, clientName, clientCur, store, onClose }) {
  const { toast } = useFeedback();
  const [pick, setPick] = useState(() => new Set([...issues.junk.map((x) => x.p.id), ...issues.dups.flatMap((g) => g.remove.map((p) => p.id))]));
  const flip = (id) =>
    setPick((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const money = (p) => fmtMoney(num(p.rate), clientCur[p.clientId] || store.settings.defaults.currency);
  const Row = ({ p, note, keep }) => (
    <label className={cx("flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50", pick.has(p.id) && "bg-red-50/50")}>
      <input type="checkbox" checked={pick.has(p.id)} onChange={() => flip(p.id)} />
      <span className="min-w-0 flex-1">
        <span className={cx("font-medium", pick.has(p.id) && "text-slate-500 line-through")}>{projectLine(p)}</span>
        <span className="ml-2 text-xs text-slate-500">{p.clientId ? clientName[p.clientId] : "Any client"} · {BILLING_SHORT[p.billing] || p.billing} · {p.status}</span>
        {note && <span className="block text-[11px] text-amber-700">{note}</span>}
      </span>
      {keep && !pick.has(p.id) && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">KEEP</span>}
      <span className="tabular-nums text-slate-700">{money(p)}</span>
    </label>
  );
  const remove = () => {
    const ids = [...pick];
    if (!ids.length) return onClose();
    const tid = store.deleteProjects(ids);
    toast(`Removed ${ids.length} entr${ids.length === 1 ? "y" : "ies"} – they're in the recycle bin`, { tone: "success", action: { label: "Undo", onClick: () => store.restoreTrash(tid) } });
    onClose();
  };
  return (
    <Modal
      open
      wide
      onClose={onClose}
      title="Clean up projects & services"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={remove} disabled={!pick.size} data-testid="cleanup-remove">
            Remove {pick.size} ticked
          </Button>
        </>
      }
    >
      <div className="max-h-[60vh] space-y-5 overflow-auto" data-testid="cleanup-modal">
        {issues.junk.length > 0 && (
          <section>
            <h3 className="mb-1 text-sm font-semibold text-slate-900">Not real projects ({issues.junk.length})</h3>
            <p className="mb-2 text-xs text-slate-500">Text read from an invoice’s bank, tax or footer section.</p>
            <div className="divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200">
              {issues.junk.map(({ p, reason }) => (
                <Row key={p.id} p={p} note={reason} />
              ))}
            </div>
          </section>
        )}
        {issues.dups.length > 0 && (
          <section>
            <h3 className="mb-1 text-sm font-semibold text-slate-900">Duplicates ({issues.dups.length} group{issues.dups.length === 1 ? "" : "s"})</h3>
            <p className="mb-2 text-xs text-slate-500">The same name twice for the same client, or an “any client” copy of a client’s project. The best copy (active, priced, monthly) is kept.</p>
            <div className="space-y-2">
              {issues.dups.map((g) => (
                <div key={g.items.map((p) => p.id).join()} className="divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200">
                  {g.keep.map((p) => (
                    <Row key={p.id} p={p} keep />
                  ))}
                  {g.remove.map((p) => (
                    <Row key={p.id} p={p} note={!p.clientId && g.keep.some((k) => k.clientId) ? "Generic copy – the client already has this" : "Duplicate"} />
                  ))}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
}

export default function ProjectsPage() {
  return (
    <Suspense fallback={<Loading />}>
      <ProjectsInner />
    </Suspense>
  );
}
